import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { toBigValue } from '@ui/lib/bignumber';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import TradingPortalFacet from '@ui/services/contract/abis/TradingPortalFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { getTradingPortalFacet } from '@ui/services/contract/contract-instances/getTradingPortalFacet';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { PayablePoolToken } from '@ui/types/token';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { increaseGasLimit } from '@ui/utils/increaseGastLimit';
import BigNumber from 'bignumber.js';
import { useMemo, useState } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

export const useDepositCollateral = (opts?: TxnMutateCallBackParams) => {
  const { writeContract, isPending } = useTxnWriteContract(opts);
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const { address: account } = useAccount();

  const { isSimulating, simulate } = useSimulateMutation();

  const mutationToastInfo = useMutationToast();
  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingAddMargin: isSubmittingAddMarginV2,
    sendAddMargin: sendAddMarginV2,
  } = useOneClickTradingV2();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] = useState(false);

  return useMemo(() => {
    const addr = getContractAddress('LeverUp', chainId);
    if (!addr) {
      throw new Error('leverup contract address not found');
    }

    return {
      isPending: isSimulating || isPending || isSubmittingAddMarginV2 || isAwaitingOneClickResult,
      async writeContract(param: {
        tradeHash: Position['id'];
        payToken: PayablePoolToken;
        amount: BigNumber;
      }) {
        const amountIn = toBigValue(param.amount, param.payToken.decimals);

        // OneClick V2 path (priority)
        if (canUseOneClickTradingV2 && !param.payToken.isNative) {
          setIsAwaitingOneClickResult(true);
          mutationToastInfo.onMutate({
            title: 'In Progress',
            description: '⚡️ One-click V2 add margin in progress',
          });

          try {
            const intentHash = await sendAddMarginV2(
              param.tradeHash,
              param.payToken.address as `0x${string}`,
              amountIn,
            );

            const status = await pollV2IntentStatus(intentHash);
            if (!status.success) {
              throw new Error(
                status.reason || 'One-click V2 add margin execution failed',
              );
            }

            mutationToastInfo.onSuccess({
              title: '⚡️ Completed',
              description: 'Margin added via one-click V2',
              txHash: status.txnHash as `0x${string}`,
            });

            return status.txnHash;
          } catch (error) {
            mutationToastInfo.onError({
              errorMsg:
                error instanceof Error
                  ? extractErrorMessage(error.message) ?? error.message
                  : undefined,
              onErrorToastOpts: {
                description: 'One-click V2 add margin failed, please try again',
              },
            });
            throw error;
          } finally {
            mutationToastInfo.onSettled();
            setIsAwaitingOneClickResult(false);
          }
        }

        // On-chain path
        if (!client) return;

        let estimateGasResult;

        const { isSuccess } = await simulate(async () => {
          const contract = getTradingPortalFacet({
            chainId,
            publicClient: client,
          });

          estimateGasResult = await contract.estimateGas.addMargin(
            [param.tradeHash, param.payToken.address, amountIn],
            {
              account: account,
              value: param.payToken.isNative ? amountIn : undefined,
            },
          );
        });
        if (!isSuccess) {
          return;
        }

        // Calculate gas limit with 10% buffer
        const gasLimit = increaseGasLimit(estimateGasResult);

        if (param.payToken.isNative) {
          return writeContract({
            abi: TradingPortalFacet,
            address: addr,
            functionName: 'addMargin',
            // liang: the function is a `payable`, when calling with gas token `value` and `amountIn`, will only charge gas token
            args: [param.tradeHash, param.payToken.address, amountIn],
            value: amountIn,
            gas: gasLimit,
          });
        } else {
          return writeContract({
            abi: TradingPortalFacet,
            address: addr,
            functionName: 'addMargin',
            args: [param.tradeHash, param.payToken.address, amountIn],
            gas: gasLimit,
          });
        }
      },
    };
  }, [
    chainId,
    isSimulating,
    isPending,
    isSubmittingAddMarginV2,
    isAwaitingOneClickResult,
    client,
    simulate,
    account,
    writeContract,
    canUseOneClickTradingV2,
    mutationToastInfo,
    sendAddMarginV2,
  ]);
};
